import type { ServerResponse } from './normalizeServerRequest';

export interface SentryEvent {
  message?: string;
  tags?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  request?: {
    headers?: Record<string, string>;
  };
  exception?: {
    values?: Array<{ type?: string; value?: string }>;
  };
}

export interface SentrySafeRequest {
  endpoint?: string;
  method?: string;
  path?: Record<string, unknown>;
  query?: Record<string, unknown>;
  statusCode?: number;
}

export interface SentryApiContext extends SentrySafeRequest {
  returnCode?: number;
  returnMessage?: string;
}

export const SENTRY_EVENT_SOURCE_TAG = 'source';
export const SENTRY_ERROR_BOUNDARY_SOURCE = 'error_boundary';

/**
 * 민감한 query 파라미터 키 목록 (대소문자 무시)
 */
const SENSITIVE_KEYS = [
  'token',
  'accessToken',
  'access_token',
  'refresh_token',
  'refreshToken',
  'api_key',
  'apiKey',
  'secret',
  'password',
  'pwd',
  'auth',
  'authorization',
  'credential',
  'session',
  'sid',
];

const EXPECTED_API_STATUS_CODES = new Set([404, 410]);

const EXPECTED_NORMALIZED_ERROR_MESSAGES = [
  '더이상 제공하지 않는 페이지 입니다',
  '상세페이지를 찾을 수 없습니다',
  '현재 진행중인 이벤트가 없어요',
  '상품을 찾을 수 없습니다',
  '상품 정보를 불러오는데 실패했습니다',
  '답례품을 찾을 수 없습니다',
  '고향사랑 지정사업을 조회할 수 없습니다',
  '존재하지 않는 상세 키 입니다',
  '응급실 정보를 찾을 수 없습니다',
  '본인인증을 하지 않은 고객입니다',
  'institutioneventdata is not found',
];

const CLIENT_ENVIRONMENT_NOISE_MESSAGES = [
  'invalid call to runtime.sendmessage(). tab not found',
  'could not establish connection. receiving end does not exist',
  'darkreader',
  'talisman extension',
  'tronlinkparams',
  'unsafewindow',
  '__firefox__',
  'webviewfinishload',
];

const THIRD_PARTY_RESOURCE_ERROR_HOSTS = [
  'googleads.g.doubleclick.net',
  'www.googleadservices.com',
  'www.googletagmanager.com',
];

const toNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);

    return Number.isNaN(parsed) ? undefined : parsed;
  }

  return undefined;
};

const getSerializedResponse = (
  event: SentryEvent,
): ServerResponse | undefined => event.extra?.__serialized__ as ServerResponse;

const getEventText = (event: SentryEvent): string => {
  const serialized = getSerializedResponse(event);
  const values = event.exception?.values ?? [];

  return [
    event.message,
    serialized?.return_message,
    ...values.flatMap((value) => [value.type, value.value]),
  ]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
};

const isNormalizedApiError = (event: SentryEvent): boolean => {
  const serialized = getSerializedResponse(event);
  if (serialized?.return_code) return true;

  return (
    event.exception?.values?.some((value) =>
      ['Client Error', 'Server Error', 'Request Error'].includes(
        value.type ?? '',
      ),
    ) ?? false
  );
};

const getApiStatusCode = (event: SentryEvent): number | undefined => {
  const serialized = getSerializedResponse(event);

  return (
    toNumber(serialized?.request?.statusCode) ??
    toNumber(event.tags?.statusCode)
  );
};

export const isSentryErrorBoundaryEvent = (event: SentryEvent): boolean =>
  event.tags?.[SENTRY_EVENT_SOURCE_TAG] === SENTRY_ERROR_BOUNDARY_SOURCE;

export const shouldDropSentryEvent = (event: SentryEvent): boolean => {
  if (isSentryErrorBoundaryEvent(event)) return false;

  const eventText = getEventText(event);
  const statusCode = getApiStatusCode(event);

  if (statusCode !== undefined && EXPECTED_API_STATUS_CODES.has(statusCode)) {
    return true;
  }

  if (
    isNormalizedApiError(event) &&
    EXPECTED_NORMALIZED_ERROR_MESSAGES.some((message) =>
      eventText.includes(message.toLowerCase()),
    )
  ) {
    return true;
  }

  if (
    CLIENT_ENVIRONMENT_NOISE_MESSAGES.some((message) =>
      eventText.includes(message),
    )
  ) {
    return true;
  }

  return THIRD_PARTY_RESOURCE_ERROR_HOSTS.some((host) =>
    eventText.includes(host),
  );
};

/**
 * query 객체에서 민감한 키를 필터링
 * @example { token: 'xxx', page: 1 } → { page: 1 }
 */
export const filterSensitiveQuery = (
  query: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  if (!query) return undefined;

  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(query)) {
    const lowerKey = key.toLowerCase();
    if (!SENSITIVE_KEYS.some((sensitive) => lowerKey.includes(sensitive))) {
      filtered[key] = value;
    }
  }

  return Object.keys(filtered).length > 0 ? filtered : undefined;
};

/**
 * endpoint URL에서 query string의 민감 정보를 마스킹
 * @example /api/user?token=xxx&page=1 → /api/user?page=1
 */
export const sanitizeEndpoint = (endpoint: string): string => {
  const [path, queryString] = endpoint.split('?');
  if (!queryString) return endpoint;

  const params = new URLSearchParams(queryString);
  const sensitiveParams: string[] = [];

  params.forEach((_, key) => {
    const lowerKey = key.toLowerCase();
    if (SENSITIVE_KEYS.some((sensitive) => lowerKey.includes(sensitive))) {
      sensitiveParams.push(key);
    }
  });

  sensitiveParams.forEach((key) => params.delete(key));

  const sanitized = params.toString();

  return sanitized ? `${path}?${sanitized}` : path;
};

/**
 * path 객체에서 민감한 키를 필터링
 * @example { userId: 123, tokenId: 'secret' } → { userId: 123 }
 */
export const filterSensitivePath = (
  path: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  if (!path) return undefined;

  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(path)) {
    const lowerKey = key.toLowerCase();
    if (!SENSITIVE_KEYS.some((sensitive) => lowerKey.includes(sensitive))) {
      filtered[key] = value;
    }
  }

  return Object.keys(filtered).length > 0 ? filtered : undefined;
};

/**
 * endpoint의 숫자 ID를 {id}로 치환하여 동일 이슈 그룹화
 * @example /api/donation/123 → /api/donation/{id}
 */
export const normalizeEndpointForTag = (
  endpoint: string,
  alreadySanitized = false,
): string => {
  const sanitized = alreadySanitized ? endpoint : sanitizeEndpoint(endpoint);
  const path = sanitized.split('?')[0];

  return path.replace(/\/\d+(?=\/|$)/g, '/{id}');
};

/**
 * Sentry event에 API 요청 컨텍스트를 추가하는 공통 로직
 * - endpoint, method, statusCode를 태그로 추가
 * - apiRequest context에 상세 정보 추가
 * - return_code 기반 에러 분류 (FRONTEND/BACKEND)
 */
export const enrichSentryEventWithApiContext = <T extends SentryEvent>(
  event: T,
): T => {
  const serialized = event.extra?.__serialized__ as ServerResponse | undefined;

  const request = serialized?.request;
  const isNormalizedError = !!serialized?.return_code;

  // 1. endpoint 태그 및 컨텍스트 추가
  if (request?.endpoint) {
    const sanitizedEndpoint = sanitizeEndpoint(request.endpoint);
    const endpointTag = normalizeEndpointForTag(sanitizedEndpoint, true);

    event.tags = {
      ...event.tags,
      endpoint: endpointTag,
      ...(request.method && { method: request.method }),
      ...(request.statusCode && { statusCode: request.statusCode }),
    };

    const apiContext: SentryApiContext = {
      endpoint: sanitizedEndpoint,
      method: request.method,
      path: filterSensitivePath(request.path),
      query: filterSensitiveQuery(request.query),
      statusCode: request.statusCode,
    };

    if (serialized?.return_code) {
      apiContext.returnCode = serialized.return_code;
    }
    if (serialized?.return_message) {
      apiContext.returnMessage = serialized.return_message;
    }

    event.contexts = {
      ...event.contexts,
      apiRequest: apiContext,
    };
  }

  // 2. 에러 분류 태그 추가
  if (isNormalizedError) {
    const { return_code, return_message } = serialized;
    event.message = return_message;

    const code = return_code ?? 40000;
    event.tags = {
      ...event.tags,
      code,
      createdOn: code >= 40000 ? 'FRONTEND' : 'BACKEND',
    };
  } else {
    event.tags = {
      ...event.tags,
      createdOn: 'UNKNOWN',
    };
  }

  return event;
};
